/**
 * `/mulai` — public page for visitors who are not programmers (non-tech F0.3).
 * Landing-styled (`.mk`), no jargon: the same three doors as the end of the wizard, explained
 * before signing in. Every CTA goes to sign-in with a redirect back into the plan flow.
 */
import { createFileRoute } from '@tanstack/react-router'
import { Bot, HeartHandshake, Wrench } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { Container, MkFooter, MkLink, MkNav, Overline, SectionHead } from '#/components/landing/mk'
import { REVIEW_PACKAGES, formatReviewPriceShort } from '#/lib/guided-pricing'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/mulai')({
  head: () => ({
    meta: [
      { title: m.simple_mulai_meta_title() },
      { name: 'description', content: m.simple_mulai_meta_desc() },
      { property: 'og:title', content: m.simple_mulai_meta_title() },
      { property: 'og:description', content: m.simple_mulai_meta_desc() },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://bystep.dev/brand/bystep-og.png' },
    ],
  }),
  component: MulaiPage,
})

function MulaiPage() {
  return (
    <div className="mk h-full overflow-y-auto overflow-x-hidden scroll-smooth">
      <MkNav />
      <main>
        <section className="pt-16 pb-12 lg:pt-24">
          <Container className="flex flex-col items-center gap-6 text-center">
            <Overline>{m.simple_mulai_how()}</Overline>
            <h1 className="max-w-[18ch]">{m.simple_mulai_h1()}</h1>
            <p className="mk-lead max-w-[56ch]">{m.simple_mulai_sub()}</p>
            <div className="mt-2 flex flex-wrap justify-center gap-3">
              <MkLink to="/sign-in" search={{ redirect: '/plan?flow=new' }}>
                {m.simple_mulai_cta()}
              </MkLink>
              <MkLink to="/otomatis" variant="secondary" arrow>
                {m.simple_mulai_auto_link()}
              </MkLink>
            </div>
            <p className="text-[13px] text-[var(--mk-fg-muted)]">{m.simple_mulai_cta_note()}</p>
          </Container>
        </section>

        <section className="py-16 lg:py-24">
          <Container className="flex flex-col gap-10">
            <SectionHead title={m.simple_doors_title()} lead={m.simple_doors_subtitle()} />
            <ul className="grid gap-4 lg:grid-cols-3">
              <DoorCol icon={<Bot className="size-5" aria-hidden />} title={m.simple_door_auto_title()} desc={m.simple_door_auto_desc()} extra={m.simple_door_auto_soon_1()} to="/otomatis" cta={m.simple_oto_cta()} />
              <DoorCol icon={<HeartHandshake className="size-5" aria-hidden />} title={m.simple_door_guided_title()} desc={m.simple_door_guided_desc()} extra={`${m.simple_door_guided_p1({ price: formatReviewPriceShort(REVIEW_PACKAGES.review15.priceIdr) })} · ${m.simple_door_guided_p2({ price: formatReviewPriceShort(REVIEW_PACKAGES.polish.priceIdr) })} · ${m.simple_door_guided_p3({ price: formatReviewPriceShort(REVIEW_PACKAGES.pair60.priceIdr) })}`} to="/consulting" cta={m.simple_door_guided_cta()} />
              <DoorCol icon={<Wrench className="size-5" aria-hidden />} title={m.simple_door_manual_title()} desc={m.simple_door_manual_desc()} extra={m.simple_mulai_note()} to="/docs" cta={m.mk_nav_docs()} />
            </ul>
            <p className="text-center text-[15px] text-[var(--mk-fg-muted)]">{m.simple_mulai_note()}</p>
            {/* Parity P0 — the owner's own subscription on their own computer */}
            <p className="text-center text-[14px]">
              <Link to="/serve" className="text-[var(--mk-accent)] underline-offset-4 hover:underline" data-testid="mulai-serve-link">
                {m.serve_mulai_link()}
              </Link>
            </p>
            <div className="flex justify-center">
              <MkLink to="/sign-in" search={{ redirect: '/plan?flow=new' }}>
                {m.simple_mulai_cta()}
              </MkLink>
            </div>
          </Container>
        </section>
      </main>
      <MkFooter />
    </div>
  )
}

function DoorCol({ icon, title, desc, extra, to, cta }: { icon: React.ReactNode; title: string; desc: string; extra: string; to: '/otomatis' | '/consulting' | '/docs'; cta: string }) {
  return (
    <li className="mk-card-white flex flex-col gap-3 p-6">
      <span className="text-[var(--mk-accent)]">{icon}</span>
      <h3 className="font-heading text-lg font-semibold tracking-[-0.02em]">{title}</h3>
      <p className="mk-muted leading-relaxed">{desc}</p>
      <p className="text-[13px] text-[var(--mk-fg-muted)]">{extra}</p>
      <MkLink to={to} variant="secondary" arrow className="mt-auto self-start">
        {cta}
      </MkLink>
    </li>
  )
}
